import React, { useState, useEffect } from 'react';

function AdminPanel({ user, mode = 'vouchers' }) {
  // If mode is 'vouchers': tabs are [vouchers, notify]
  // If mode is 'newsletter': tabs are [newsletter, subscribers]
  const defaultTab = mode === 'newsletter' ? 'newsletter' : 'vouchers';
  
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [vouchers, setVouchers] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Form states
  const [voucherForm, setVoucherForm] = useState({
    code: '',
    discount_percent: '',
    discount_amount: '',
    customer_email: '',
    description: '',
    expires_at: ''
  });

  const [notificationForm, setNotificationForm] = useState({
    voucher_id: '',
    customer_email: '',
    message: ''
  });

  const [newsletterForm, setNewsletterForm] = useState({
    subject: '',
    message: ''
  });

  // Fetch vouchers
  useEffect(() => {
    if (activeTab === 'vouchers') {
      fetchVouchers();
    } else if (activeTab === 'subscribers') {
      fetchSubscribers();
    }
  }, [activeTab]);

  const fetchVouchers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `/api/admin/vouchers?email=${user.email}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      const data = await response.json();
      if (data.success) {
        setVouchers(data.vouchers);
      }
    } catch (err) {
      console.error('Error fetching vouchers:', err);
      setMessage('❌ Fehler beim Abrufen von Gutscheinen');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscribers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `/api/newsletter/subscribers?email=${user.email}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      const data = await response.json();
      if (data.success) {
        setSubscribers(data.subscribers);
      }
    } catch (err) {
      console.error('Error fetching subscribers:', err);
      setMessage('❌ Fehler beim Abrufen von Abonnenten');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVoucher = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/vouchers/create', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email: user.email,
          ...voucherForm
        })
      });

      const data = await response.json();
      if (data.success) {
        setMessage(`✅ Gutschein erstellt: ${data.code}`);
        setVoucherForm({
          code: '',
          discount_percent: '',
          discount_amount: '',
          customer_email: '',
          description: '',
          expires_at: ''
        });
        fetchVouchers();
      } else {
        setMessage(`❌ ${data.error}`);
      }
    } catch (err) {
      setMessage('❌ Fehler beim Erstellen des Gutscheins');
    } finally {
      setLoading(false);
    }
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        '/api/admin/send-voucher-notification',
        {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            email: user.email,
            ...notificationForm
          })
        }
      );

      const data = await response.json();
      if (data.success) {
        setMessage(`✅ ${data.message}`);
        setNotificationForm({
          voucher_id: '',
          customer_email: '',
          message: ''
        });
      } else {
        setMessage(`❌ ${data.error}`);
      }
    } catch (err) {
      setMessage('❌ Fehler beim Versenden der Benachrichtigung');
    } finally {
      setLoading(false);
    }
  };

  const handleSendNewsletter = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/newsletter/send', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email: user.email,
          ...newsletterForm
        })
      });

      const data = await response.json();
      if (data.success) {
        setMessage(`✅ ${data.message}`);
        setNewsletterForm({ subject: '', message: '' });
      } else {
        setMessage(`❌ ${data.error}`);
      }
    } catch (err) {
      setMessage('❌ Fehler beim Versenden des Newsletters');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVoucher = async (voucherId) => {
    if (!window.confirm('⚠️ Gutschein wirklich löschen?')) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`/api/admin/vouchers/${voucherId}?email=${encodeURIComponent(user.email)}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setMessage('✅ Gutschein gelöscht');
        fetchVouchers();
      } else {
        setMessage(`❌ ${data.error}`);
      }
    } catch (err) {
      setMessage('❌ Fehler beim Löschen des Gutscheins');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-panel">
      <div className="admin-tabs">
        {/* VOUCHERS MODE: Show Gutscheine + Benachrichtigungen */}
        {mode === 'vouchers' && (
          <>
            <button
              className={`admin-tab ${activeTab === 'vouchers' ? 'active' : ''}`}
              onClick={() => setActiveTab('vouchers')}
            >
              🎫 Gutscheine
            </button>
            <button
              className={`admin-tab ${activeTab === 'notify' ? 'active' : ''}`}
              onClick={() => setActiveTab('notify')}
            >
              📧 Benachrichtigungen
            </button>
          </>
        )}

        {/* NEWSLETTER MODE: Show Newsletter + Abonnenten */}
        {mode === 'newsletter' && (
          <>
            <button
              className={`admin-tab ${activeTab === 'newsletter' ? 'active' : ''}`}
              onClick={() => setActiveTab('newsletter')}
            >
              📰 Newsletter
            </button>
            <button
              className={`admin-tab ${activeTab === 'subscribers' ? 'active' : ''}`}
              onClick={() => setActiveTab('subscribers')}
            >
              👥 Abonnenten
            </button>
          </>
        )}
      </div>

      {message && (
        <div className="admin-message">
          {message}
          <button onClick={() => setMessage('')}>×</button>
        </div>
      )}

      {/* GUTSCHEINE TAB */}
      {activeTab === 'vouchers' && (
        <div className="admin-section">
          <h3>🎫 Gutscheinverwaltung</h3>

          <form onSubmit={handleCreateVoucher} className="admin-form">
            <h4>Neuen Gutschein erstellen</h4>
            <div className="form-group">
              <label>Gutschein-Code *</label>
              <input
                type="text"
                placeholder="z.B. SAVE20"
                value={voucherForm.code}
                onChange={(e) => setVoucherForm({ ...voucherForm, code: e.target.value })}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Rabatt (%)</label>
                <input
                  type="number"
                  placeholder="z.B. 20"
                  value={voucherForm.discount_percent}
                  onChange={(e) =>
                    setVoucherForm({ ...voucherForm, discount_percent: e.target.value })
                  }
                />
              </div>
              <div className="form-group">
                <label>Rabatt (€)</label>
                <input
                  type="number"
                  placeholder="z.B. 10.00"
                  step="0.01"
                  value={voucherForm.discount_amount}
                  onChange={(e) =>
                    setVoucherForm({ ...voucherForm, discount_amount: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="form-group">
              <label>Kundenmail (optional)</label>
              <input
                type="email"
                placeholder="kunde@example.com"
                value={voucherForm.customer_email}
                onChange={(e) =>
                  setVoucherForm({ ...voucherForm, customer_email: e.target.value })
                }
              />
            </div>

            <div className="form-group">
              <label>Beschreibung (optional)</label>
              <textarea
                placeholder="z.B. Neukunden-Rabatt"
                value={voucherForm.description}
                onChange={(e) =>
                  setVoucherForm({ ...voucherForm, description: e.target.value })
                }
              ></textarea>
            </div>

            <div className="form-group">
              <label>Ablaufdatum (optional)</label>
              <input
                type="datetime-local"
                value={voucherForm.expires_at}
                onChange={(e) =>
                  setVoucherForm({ ...voucherForm, expires_at: e.target.value })
                }
              />
              <small style={{ color: '#94a3b8', marginTop: '5px', display: 'block' }}>
                Leer = Keine Zeitbegrenzung
              </small>
            </div>

            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? 'Erstelle...' : '➕ Gutschein erstellen'}
            </button>
          </form>

          <div className="vouchers-list">
            <h4>Bestehende Gutscheine ({vouchers.length})</h4>
            {vouchers.length === 0 ? (
              <p className="no-data">Noch keine Gutscheine erstellt</p>
            ) : (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Rabatt</th>
                    <th>Kunde</th>
                    <th>Status</th>
                    <th>Erstellt</th>
                    <th>Ablauf</th>
                    <th>Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {vouchers.map((voucher) => {
                    const isExpired = voucher.expires_at && new Date(voucher.expires_at) < new Date();
                    const daysUntilExpiry = voucher.expires_at 
                      ? Math.ceil((new Date(voucher.expires_at) - new Date()) / (1000 * 60 * 60 * 24))
                      : null;
                    
                    return (
                      <tr key={voucher.id} style={isExpired ? { opacity: 0.5 } : {}}>
                        <td>
                          <strong>{voucher.code}</strong>
                          {isExpired && <span style={{ marginLeft: '8px', color: '#ef4444' }}>⏳ Abgelaufen</span>}
                        </td>
                        <td>
                          {voucher.discount_percent > 0 && `${voucher.discount_percent}%`}
                          {voucher.discount_amount > 0 && `${voucher.discount_amount}€`}
                        </td>
                        <td>{voucher.customer_email || '-'}</td>
                        <td>
                          <span className={`status ${voucher.status}`}>{voucher.status}</span>
                        </td>
                        <td>{new Date(voucher.created_at).toLocaleDateString('de-DE')}</td>
                        <td>
                          {voucher.expires_at ? (
                            <div>
                              <small>{new Date(voucher.expires_at).toLocaleDateString('de-DE')}</small>
                              <br />
                              <small style={{ color: isExpired ? '#ef4444' : daysUntilExpiry < 7 ? '#f59e0b' : '#10b981' }}>
                                {isExpired ? 'Abgelaufen' : `${daysUntilExpiry} Tage`}
                              </small>
                            </div>
                          ) : (
                            <small style={{ color: '#94a3b8' }}>Unbegrenzt</small>
                          )}
                        </td>
                        <td>
                          <button
                            onClick={() => handleDeleteVoucher(voucher.id)}
                            disabled={loading}
                            style={{
                              background: '#ef4444',
                              color: 'white',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}
                          >
                            🗑️ Löschen
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* BENACHRICHTIGUNGEN TAB */}
      {activeTab === 'notify' && (
        <div className="admin-section">
          <h3>📧 Gutschein-Benachrichtigungen</h3>

          <form onSubmit={handleSendNotification} className="admin-form">
            <h4>Nachricht an Kunde senden</h4>
            <div className="form-group">
              <label>Gutschein auswählen *</label>
              <select
                value={notificationForm.voucher_id}
                onChange={(e) =>
                  setNotificationForm({ ...notificationForm, voucher_id: e.target.value })
                }
                required
              >
                <option value="">-- Gutschein auswählen --</option>
                {vouchers.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.code} (
                    {v.discount_percent > 0 && `${v.discount_percent}%`}
                    {v.discount_amount > 0 && `${v.discount_amount}€`})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Kundenmail *</label>
              <input
                type="email"
                placeholder="kunde@example.com"
                value={notificationForm.customer_email}
                onChange={(e) =>
                  setNotificationForm({ ...notificationForm, customer_email: e.target.value })
                }
                required
              />
            </div>

            <div className="form-group">
              <label>Nachricht (optional)</label>
              <textarea
                placeholder="Ihr Gutschein kann jetzt verwendet werden!"
                value={notificationForm.message}
                onChange={(e) =>
                  setNotificationForm({ ...notificationForm, message: e.target.value })
                }
              ></textarea>
            </div>

            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? 'Wird versendet...' : '✉️ Nachricht versenden'}
            </button>
          </form>

          <div className="notify-info">
            <p>💡 Die Benachrichtigung wird per E-Mail an den Kunden versendet.</p>
          </div>
        </div>
      )}

      {/* NEWSLETTER TAB */}
      {activeTab === 'newsletter' && (
        <div className="admin-section">
          <h3>📰 Newsletter versenden</h3>

          <form onSubmit={handleSendNewsletter} className="admin-form">
            <div className="form-group">
              <label>Betreff *</label>
              <input
                type="text"
                placeholder="z.B. Wichtige Updates von TimeLock"
                value={newsletterForm.subject}
                onChange={(e) =>
                  setNewsletterForm({ ...newsletterForm, subject: e.target.value })
                }
                required
              />
            </div>

            <div className="form-group">
              <label>Nachricht *</label>
              <textarea
                placeholder="Schreiben Sie Ihre Newsletter-Nachricht..."
                value={newsletterForm.message}
                onChange={(e) =>
                  setNewsletterForm({ ...newsletterForm, message: e.target.value })
                }
                required
                rows="8"
              ></textarea>
            </div>

            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? 'Wird versendet...' : '📤 An alle versenden'}
            </button>
          </form>

          <div className="newsletter-info">
            <p>📊 Newsletter wird an {subscribers.length} aktive Abonnenten versendet.</p>
            <p>💬 Über neue Features, Updates und Kapazitätsstatus informieren.</p>
          </div>
        </div>
      )}

      {/* ABONNENTEN TAB */}
      {activeTab === 'subscribers' && (
        <div className="admin-section">
          <h3>👥 Newsletter-Abonnenten</h3>

          <div className="subscribers-list">
            <p className="subscriber-count">
              Gesamt: <strong>{subscribers.length}</strong> aktive Abonnenten
            </p>

            {subscribers.length === 0 ? (
              <p className="no-data">Noch keine Newsletter-Abonnenten</p>
            ) : (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>E-Mail</th>
                    <th>Anmeldedatum</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.map((sub) => (
                    <tr key={sub.id}>
                      <td>{sub.email}</td>
                      <td>{new Date(sub.subscribed_at).toLocaleDateString('de-DE')}</td>
                      <td>
                        <span className="status active">
                          {sub.active ? '✓ Aktiv' : 'Inaktiv'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPanel;
